import { put, select, call } from 'lib/redux';
import { getCompanyInfoSaga, getOrderPreferencesSaga } from 'features/userData/UserData.saga';
import getResourceFileService from 'services/getResourceFile.service';
import updateAddressInfoService from 'services/updateAddressInfo.service';
import * as types from 'constants/ActionTypes';
import { ADDRESS_TYPES, USER_EVENTS } from 'constants/AppConstants';
import { logError } from 'utils/logger.utils';
import { trackUserEvent } from 'utils/app.utils';
import {
  loadContactAddressInfo,
  resetContactInfoPage,
  loadContactInfoStart,
  loadContactInfoSuccess,
  loadContactInfoError,
  loadContactInfoLogo,
  submitContactInfoStart,
  resetBillingSubmission,
  submitContactInfoComplete,
  submitContactInfoValidationError,
  submitContactInfoError,
} from './ContactInformation.actions';

const isNotFetched = (values) => !values || Object.keys(values).length === 0;

export function* loadContactInformationPageSaga() {
  yield put(resetContactInfoPage());
  yield put(loadContactInfoStart());
  try {
    yield getCompanyInfoSaga();
    const companyInfoResponse = yield select(({ userData }) => (userData.companyInformation));
    const { companyInformation: companyInforLoadError } = yield select(({ userData }) => (userData.error));
    if (companyInforLoadError) throw new Error('Error fetching contact information');
    yield getOrderPreferencesSaga();
    const orderPreferences = yield select(({ userData }) => (userData.orderPreferences));
    if (isNotFetched(orderPreferences)) throw new Error('Error fetching order Preferences information');
    const companyInfo = orderPreferences.reportOptionsCoverCompanyLogo;
    const { ok: logoLoadStatus, json: companyLogo } = yield call(getResourceFileService, companyInfo.value);
    if (!logoLoadStatus) {
      const error = new Error(companyLogo.errorDescription);
      error.errorCode = companyLogo.errorCode;
      yield put({ type: types.CONTACT_INFO_LOAD_COMPANY_LOGO_ERROR });
      throw error;
    }
    yield put({ type: types.CONTACT_INFO_LOAD_COMPANY_LOGO, companyLogo });
    yield put(loadContactAddressInfo(companyInfoResponse.address));
    yield put(loadContactInfoLogo(companyLogo));
    yield put(loadContactInfoSuccess());
  } catch (error) {
    logError('Error in loadContactInformationPageSaga', {
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put(loadContactInfoError());
  }
}

export const addressSelector = ({ address }) => ({
  firstName: address.firstName || '',
  lastName: address.lastName || '',
  company: address.company || '',
  emailAddress: address.emailAddress || '',
  emailCC: address.emailCC || '',
  country: address.country || '',
  department: address.department || '',
  phone: address.phone || '',
  phoneExt: address.phoneExt || '',
  secondaryPhone: address.secondaryPhone || '',
  secondaryExt: address.secondaryExt || '',
  address1: address.address1 || '',
  address2: address.address2 || '',
  city: address.city || '',
  state: address.state || '',
  zip: address.zip || '',
});

export function* submitContactInfoSaga() {
  trackUserEvent(USER_EVENTS.SAVE_CONTACT_INFO);
  yield put(submitContactInfoStart());
  yield put(resetBillingSubmission());

  try {
    const {
      addressInfo,
      addressInfoErrors,
    } = yield select(({ contactInformation }) => contactInformation);

    const isAddressInfoValid = Object.values(addressInfoErrors).every((val) => val === '');
    if (!isAddressInfoValid) {
      yield put(submitContactInfoValidationError());
      return;
    }

    const addressUpdateResponse = yield call(updateAddressInfoService, { addressType: ADDRESS_TYPES.ShipToAddress, ValidateCompany: true }, addressInfo);
    if (!addressUpdateResponse.ok) {
      const error = new Error(addressUpdateResponse.json.errorDescription);
      error.errorCode = addressUpdateResponse.json.errorCode;
      throw error;
    }
    const address = addressSelector(addressUpdateResponse.json);
    const {
      showCountry,
      adminUser,
      addressType,
    } = yield select(({ userData }) => userData.companyInformation);
    yield put({
      type: types.COMPANY_INFO_SUCCESS,
      companyInformation: {
        showCountry,
        adminUser,
        addressType,
        address,
      },
    });
    yield put(submitContactInfoComplete());
  } catch (error) {
    yield put(submitContactInfoError(error.message));
  }
}
